import { fuzzyValuesMatch, hasRuleValue, normalizeRuleText } from '../../shared/comparisons';
import { formatEvidence } from '../../shared/evidence';

import { SETUP_EVIDENCE_LABELS } from './setupEvidence';
import { buildSetupRuleContext, isTrue, schedulesExcludeSelections, statusFromCheck } from './setupRuleContext';
import type { SetupChecklistRow, SetupRuleInput } from './types';

function hasValue(value?: string | null) {
  return hasRuleValue(value);
}

function valuesMatch(left?: string | null, right?: string | null) {
  return fuzzyValuesMatch(left, right);
}

function getAccountNameSummary(
  hasAccountNameError: boolean,
  accountName?: string | null,
  propertyName?: string | null,
) {
  if (hasAccountNameError) {
    return 'Required account or property evidence could not be loaded.';
  }

  if (hasValue(accountName) && hasValue(propertyName)) {
    return 'Account and property names were compared.';
  }

  return 'Account or property name is missing.';
}

function getAbnSummary(hasAbnError: boolean, hasPropertyCountry: boolean, isAustralia: boolean) {
  if (hasAbnError) {
    return 'Required account or property evidence could not be loaded.';
  }

  if (!hasPropertyCountry) {
    return 'Property country is missing.';
  }

  return isAustralia
    ? 'Australian account ABN verification was checked.'
    : 'ABN verification is not required outside Australia.';
}

function getCampaignScheduleSummary(
  hasCampaignExclusionsError: boolean,
  hasExclusions: boolean,
  campaignScheduleCount: number,
) {
  if (hasCampaignExclusionsError) {
    return 'Required opportunity evidence could not be loaded.';
  }

  if (!hasExclusions) {
    return 'Brand and market exclusion evidence is not loaded.';
  }

  if (campaignScheduleCount === 0) {
    return 'Campaign schedule rows are not loaded.';
  }

  return 'Campaign schedules were checked against selected exclusions.';
}

export function buildSetupChecklistRules(input: SetupRuleInput): Array<SetupChecklistRow> {
  const context = buildSetupRuleContext(input);

  return [
    {
      id: 'setup-account-name',
      category: 'Setup',
      field: 'Account name',
      rule: 'Account name should match the hotel name on their website.',
      status: statusFromCheck(
        !context.hasAccountNameError &&
          hasValue(input.account?.name) &&
          hasValue(input.property?.name) &&
          normalizeRuleText(input.account?.name) === normalizeRuleText(input.property?.name),
      ),
      statusLabel: 'Account name',
      summary: getAccountNameSummary(context.hasAccountNameError, input.account?.name, input.property?.name),
      evidence: [
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.accountName,
          ...formatEvidence(input.account?.name, context.sourceErrors.account),
        },
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.propertyName,
          ...formatEvidence(input.property?.name, context.sourceErrors.property),
        },
      ],
    },
    {
      id: 'setup-account-abn',
      category: 'Setup',
      field: 'ABN',
      rule: 'Australia: ABN Verified must be ticked.',
      status: statusFromCheck(
        !context.hasAbnError &&
          context.hasPropertyCountry &&
          (!context.isAustralia || isTrue(input.account?.abnVerified)),
      ),
      statusLabel: 'ABN',
      summary: getAbnSummary(context.hasAbnError, context.hasPropertyCountry, context.isAustralia),
      evidence: [
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.propertyCountry,
          ...formatEvidence(input.property?.country, context.sourceErrors.property),
        },
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.abnVerified,
          ...formatEvidence(input.account?.abnVerified, context.sourceErrors.account),
        },
      ],
    },
    {
      id: 'setup-account-currency',
      category: 'Setup',
      field: 'Hotel account currency',
      rule: 'Hotel account currency should match the opportunity currency.',
      status: statusFromCheck(
        !context.hasAccountCurrencyError &&
          hasValue(input.account?.bankCurrency ?? input.account?.currencyIsoCode) &&
          hasValue(input.opportunity?.currencyIsoCode) &&
          normalizeRuleText(input.account?.bankCurrency ?? input.account?.currencyIsoCode) ===
            normalizeRuleText(input.opportunity?.currencyIsoCode),
      ),
      statusLabel: 'Hotel account currency',
      summary: context.hasAccountCurrencyError
        ? 'Required account or opportunity evidence could not be loaded.'
        : 'Hotel account currency was compared with opportunity currency.',
      evidence: [
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.hotelAccountCurrency,
          ...formatEvidence(
            input.account?.bankCurrency ?? input.account?.currencyIsoCode,
            context.sourceErrors.account,
          ),
        },
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.opportunityCurrency,
          ...formatEvidence(input.opportunity?.currencyIsoCode, context.sourceErrors.opportunity),
        },
      ],
    },
    {
      id: 'setup-campaign-schedules-exclusions',
      category: 'Setup',
      field: 'Campaign Schedules exclude selected brand / market',
      rule: 'Campaign schedules should not include selected brand / market exclusions.',
      status: statusFromCheck(
        !context.hasCampaignExclusionsError &&
          !!input.exclusions &&
          schedulesExcludeSelections(input.campaignSchedules, context.excludedBrands, context.excludedCountries),
      ),
      statusLabel: 'Campaign Schedules exclude selected brand / market',
      summary: getCampaignScheduleSummary(
        context.hasCampaignExclusionsError,
        !!input.exclusions,
        input.campaignSchedules?.length ?? 0,
      ),
      evidence: [
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.excludedBrands,
          ...formatEvidence(
            context.excludedBrands.length ? context.excludedBrands.join(', ') : '-',
            context.sourceErrors.opportunity,
          ),
        },
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.excludedCountries,
          ...formatEvidence(
            context.excludedCountries.length ? context.excludedCountries.join(', ') : '-',
            context.sourceErrors.opportunity,
          ),
        },
        { label: SETUP_EVIDENCE_LABELS.loadedScheduleRows, value: String(input.campaignSchedules?.length ?? 0) },
      ],
    },
    {
      id: 'setup-marketing-terms',
      category: 'Setup',
      field: 'Marketing terms',
      rule: 'Auto-approve if marketing terms are consistent with brand.',
      status: statusFromCheck(
        !context.hasMarketingTermsError && valuesMatch(input.chainName, input.account?.contractTemplateChain),
      ),
      statusLabel: 'Marketing terms',
      summary: context.hasMarketingTermsError
        ? 'Required Salesforce account evidence could not be loaded.'
        : 'Salesforce parent account was compared with the matching contract template.',
      evidence: [
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.canonicalPropertyChain,
          ...formatEvidence(input.chainName, context.sourceErrors.parentAccount),
        },
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.contractTemplateId,
          ...formatEvidence(input.account?.contractTemplateId, context.sourceErrors.account),
        },
      ],
    },
    {
      id: 'setup-contracts-geo',
      category: 'Setup',
      field: 'Geo',
      rule: 'Auto-approve if an active contract template exists for this geo.',
      status: statusFromCheck(
        !context.hasGeoError && valuesMatch(input.opportunity?.geo, input.account?.contractTemplateGeo),
      ),
      statusLabel: 'Geo',
      summary: context.hasGeoError
        ? 'Required opportunity or account evidence could not be loaded.'
        : 'LE Contracts Geo was compared with the matching contract template.',
      evidence: [
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.leContractsGeo,
          ...formatEvidence(input.opportunity?.geo, context.sourceErrors.opportunity),
        },
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.contractTemplateId,
          ...formatEvidence(input.account?.contractTemplateId, context.sourceErrors.account),
        },
      ],
    },
    {
      id: 'setup-sell-cost-reporting-currency',
      category: 'Setup',
      field: 'Sell / cost / reporting currency',
      rule: 'Auto-approve if sell, cost, and reporting currency are the same.',
      status: statusFromCheck(
        !context.hasCurrencyError &&
          hasValue(context.sellCurrency) &&
          hasValue(context.costCurrency) &&
          hasValue(context.reportingCurrency) &&
          context.uniqueComparedCurrencies.length === 1,
      ),
      statusLabel: 'Sell / cost / reporting currency',
      summary: context.hasCurrencyError
        ? 'Required opportunity or account evidence could not be loaded.'
        : 'Sell, cost, reporting, and loaded package currencies were compared.',
      evidence: [
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.sellCurrency,
          ...formatEvidence(context.sellCurrency, context.sourceErrors.opportunity),
        },
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.costCurrency,
          ...formatEvidence(context.costCurrency, context.sourceErrors.opportunity),
        },
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.reportingCurrency,
          ...formatEvidence(context.reportingCurrency, context.sourceErrors.account),
        },
        {
          label: SETUP_EVIDENCE_LABELS.packageCurrencies,
          value: context.packageCurrencies.length ? context.packageCurrencies.join(', ').toUpperCase() : '-',
        },
      ],
    },
  ];
}
